// HUD controls of the design system (components/hud/SegmentedPill.jsx, components/core/Switch.jsx,
// components/core/IconButton.jsx with components/hud/IconFrame.jsx),
// rebuilt with plain DOM. Styles live in src/styles/hud.css.
import { h, icon } from './dom';

export interface PillOption<T extends string> {
  readonly id: T;
  /** Text shown, or the name read out for an icon option. */
  readonly label: string;
  /** An icon shown instead of the text. */
  readonly icon?: string;
}

export interface Control<T> {
  readonly el: HTMLElement;
  set(value: T): void;
}

/** A capsule of mutually exclusive options; the active one is gold, or white with `tone: 'white'`. */
export function segmentedPill<T extends string>(opts: {
  readonly label: string;
  readonly options: readonly PillOption<T>[];
  readonly value: T;
  readonly tone?: 'gold' | 'white';
  readonly size?: 'md' | 'sm';
  readonly onChange: (value: T) => void;
}): Control<T> {
  const group = h('div', { class: `pill pill--${opts.tone ?? 'gold'} pill--${opts.size ?? 'md'}`, role: 'radiogroup', 'aria-label': opts.label });
  const buttons = opts.options.map((o) => {
    const b = o.icon
      ? h('button', { type: 'button', role: 'radio', class: 'pill__option', 'data-value': o.id, 'aria-label': o.label }, icon(o.icon, 'icon pill__icon'))
      : h('button', { type: 'button', role: 'radio', class: 'pill__option', 'data-value': o.id }, o.label);
    b.addEventListener('click', () => {
      set(o.id);
      opts.onChange(o.id);
    });
    group.append(b);
    return b;
  });
  const set = (value: T): void => {
    for (const b of buttons) b.setAttribute('aria-checked', String(b.dataset.value === value));
  };
  set(opts.value);
  return { el: group, set };
}

/** A gold on/off switch with a 44 px hit area. */
export function toggleSwitch(opts: { readonly label: string; readonly checked: boolean; readonly onChange: (checked: boolean) => void }): Control<boolean> {
  const button = h('button', { type: 'button', role: 'switch', class: 'switch', 'aria-label': opts.label }, h('span', { class: 'switch__track' }, h('span', { class: 'switch__thumb' })));
  let checked = opts.checked;
  const set = (value: boolean): void => {
    checked = value;
    button.setAttribute('aria-checked', String(value));
  };
  button.addEventListener('click', () => {
    set(!checked);
    opts.onChange(checked);
  });
  set(checked);
  return { el: button, set };
}

/**
 * An icon in the squircle IconFrame that toggles something open: the design system's framed IconButton,
 * with its `active` (white ring and glow) shown while `pressed` is true. The parent sets the state.
 */
export function frameButton(opts: { readonly label: string; readonly icon: string; readonly pressed: boolean; readonly onClick: () => void }): Control<boolean> {
  const button = h('button', { type: 'button', class: 'frame-button', 'aria-label': opts.label }, icon(opts.icon, 'icon frame-button__icon'));
  const set = (value: boolean): void => {
    button.setAttribute('aria-pressed', String(value));
  };
  button.addEventListener('click', opts.onClick);
  set(opts.pressed);
  return { el: button, set };
}

/** The design system's IconFrame: a squircle with a light-blue ring and a navy fill, around any content. */
export function squircle(content: Node, opts: { readonly size: number; readonly tone?: 'navy' | 'gold'; readonly className?: string } = { size: 52 }): HTMLSpanElement {
  const el = h('span', { class: `squircle squircle--${opts.tone ?? 'navy'}${opts.className ? ` ${opts.className}` : ''}`, style: `--size:${opts.size}px` }, content);
  return el;
}

/** A design-system Button: gold primary, blue secondary, white light or a quiet ghost. */
export function button(opts: {
  readonly label: string;
  readonly icon?: string;
  readonly variant?: 'primary' | 'secondary' | 'light' | 'ghost';
  readonly size?: 'lg' | 'md' | 'sm';
  readonly onClick: () => void;
}): HTMLButtonElement {
  const b = h('button', { type: 'button', class: `button button--${opts.variant ?? 'primary'} button--${opts.size ?? 'md'}` }, ...(opts.icon ? [icon(opts.icon, 'icon button__icon')] : []), h('span', {}, opts.label));
  b.addEventListener('click', opts.onClick);
  return b;
}

/** The design system's ProgressBar: a dark track with a light, amber, gold or green fill and its label beside it. */
export function progressBar(): { readonly el: HTMLElement; set(value: number, label: string, tone?: 'light' | 'amber' | 'green'): void } {
  const fill = h('span', { class: 'progress__fill' });
  const track = h('span', { class: 'progress__track', role: 'progressbar', 'aria-valuemin': 0, 'aria-valuemax': 100 }, fill);
  const text = h('span', { class: 'progress__label' });
  const el = h('span', { class: 'progress' }, track, text);
  return {
    el,
    set: (value, label, tone = 'light') => {
      const pct = Math.max(0, Math.min(100, value * 100));
      fill.style.width = `${pct}%`;
      fill.className = `progress__fill progress__fill--${tone}`;
      track.setAttribute('aria-valuenow', String(Math.round(pct)));
      track.setAttribute('aria-label', label);
      text.textContent = label;
    },
  };
}
